/**
 * ProtectedRoute - Wraps routes that require authentication AND subscription
 * Redirects to login if not authenticated
 * Shows subscription modal if no active subscription (instead of redirect)
 *
 * NEW: Uses useAccessLevel hook for comprehensive access checking
 * including INVITED_PENDING_SETUP state detection
 *
 * NEW: Shows SubscriptionExpiringModal for expiring/expired subscriptions
 * NEW: Shows SubscriptionOnboardingModal with X button for expired users
 */

import { useState, useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { useAccessLevel } from '@/hooks/useAccessLevel'
import { SubscriptionExpiringModal } from '@/components/subscription/SubscriptionExpiringModal'
import { SubscriptionOnboardingModal } from '@/components/auth/SubscriptionOnboardingModal'

// Routes that don't require subscription (user can access to complete setup)
const ROUTES_WITHOUT_SUBSCRIPTION = ['/welcome', '/account', '/subscription', '/payment-methods', '/settings', '/wallet', '/mail', '/groupchat']

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()
  const [modalDismissed, setModalDismissed] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [accessValidated, setAccessValidated] = useState(false)

  // Use new access level hook - skip if not authenticated
  const {
    loading: accessLoading,
    hasAccess,
    requiresOnboarding,
    state,
    showBanner,
    bannerMessage,
    trialDaysRemaining,
    expiresAt,
    error: accessError,
    refresh: refreshAccessLevel
  } = useAccessLevel({ skip: !isAuthenticated || !user })

  // States that trigger the expiring modal
  const expiringStates = ['TRIAL_ENDING_SOON', 'TRIAL_EXPIRED_GRACE', 'SUBSCRIPTION_EXPIRED_GRACE']
  const shouldShowExpiringModal = !modalDismissed && expiringStates.includes(state)

  // Check if current route requires subscription
  const isAllowedRoute = ROUTES_WITHOUT_SUBSCRIPTION.some(
    route => location.pathname.startsWith(route)
  )

  // Validate access with fresh Stripe check when user tries to access protected route without access
  // This runs ONCE per route navigation, not on every hasAccess change
  useEffect(() => {
    const validateAndShowModal = async () => {
      // Only check if we haven't validated yet AND user doesn't have access
      if (!accessValidated && !accessLoading && isAuthenticated && user && !hasAccess && !isAllowedRoute && !requiresOnboarding) {
        // Mark as validated FIRST to prevent loops
        setAccessValidated(true)

        // Refresh access level to get latest from Stripe
        await refreshAccessLevel()

        // After refresh, show subscription modal (it will close if user now has access)
        setShowSubscriptionModal(true)
      }
    }

    validateAndShowModal()
  }, [accessValidated, accessLoading, isAuthenticated, user, hasAccess, isAllowedRoute, requiresOnboarding])

  // Reset validation state when route changes
  useEffect(() => {
    setAccessValidated(false)
    setShowSubscriptionModal(false)
  }, [location.pathname])

  // Still loading auth state or access level
  if (isLoading || (isAuthenticated && accessLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Error checking access - show refresh option instead of infinite spinner
  if (accessError) {
    console.error('[ProtectedRoute] Access check error:', accessError)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error checking access. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>
    )
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // User needs to complete onboarding (INVITED_PENDING_SETUP state)
  // Redirect to welcome unless already on an allowed route
  if (requiresOnboarding && !isAllowedRoute) {
    return <Navigate to="/welcome" replace />
  }

  // No access and not on allowed route - show subscription modal with X button
  // This allows expired users to see they need to subscribe without redirect loop
  if (!hasAccess && !isAllowedRoute) {
    // Handler for when subscription is completed
    const handleSubscriptionComplete = () => {
      setShowSubscriptionModal(false)
      refreshAccessLevel()
      // Reload to refresh access state
      window.location.reload()
    }

    // Handler for closing modal - stay on blocked page
    const handleModalClose = () => {
      setShowSubscriptionModal(false)
      // User closes modal - they stay but content is blocked
    }

    return (
      <div className="min-h-screen bg-gray-100">
        {/* Blocked Content Overlay */}
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Required</h2>
            <p className="text-gray-600 mb-6">
              Your subscription has expired. Subscribe to access this content.
            </p>
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
            >
              Start Your Subscription
            </button>
          </div>
        </div>

        {/* Subscription Modal with X button (for expired users) */}
        {showSubscriptionModal && user && (
          <SubscriptionOnboardingModal
            isOpen={showSubscriptionModal}
            userId={user.id}
            inviteToken={null}
            onComplete={handleSubscriptionComplete}
            onClose={handleModalClose}
            allowSkip={false}
          />
        )}
      </div>
    )
  }

  // Authenticated with access - render the protected route

  // Determine grace days remaining for modal
  const daysRemaining = trialDaysRemaining ??
    (state === 'SUBSCRIPTION_EXPIRED_GRACE' && expiresAt
      ? Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : null)

  // Optionally render warning banner if needed
  if (showBanner && bannerMessage) {
    return (
      <>
        {/* Expiring subscription modal */}
        {shouldShowExpiringModal && (
          <SubscriptionExpiringModal
            state={state}
            daysRemaining={daysRemaining}
            expiresAt={expiresAt}
            onDismiss={() => setModalDismissed(true)}
          />
        )}
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center">
          <p className="text-sm text-yellow-800">{bannerMessage}</p>
        </div>
        <Outlet />
      </>
    )
  }

  return (
    <>
      {/* Expiring subscription modal */}
      {shouldShowExpiringModal && (
        <SubscriptionExpiringModal
          state={state}
          daysRemaining={daysRemaining}
          expiresAt={expiresAt}
          onDismiss={() => setModalDismissed(true)}
        />
      )}
      <Outlet />
    </>
  )
}
